import {
  Listbox,
  ListboxOption,
  ListboxOptions,
  Popover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react';
import { SelectorIcon as ChevronUpDownIcon } from '@heroicons/react/solid';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SearchInput } from '~/components/FindAddContact/SearchInput';
import { move } from '~/helpers/array';
import { useSortable } from '~/hooks/useSortable';

export type FacetedOption = string;

function NavTypesListbox({
  items,
  selected,
  onChange,
  labelFor,
}: {
  items: string[];
  selected: string[];
  onChange: (v: string[]) => void;
  labelFor?: (value: string) => string;
}) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((n) => {
      const base = n.toLowerCase();
      const label = (labelFor ? labelFor(n) : n).toLowerCase();
      return base.includes(q) || label.includes(q);
    });
  }, [items, query, labelFor]);

  return (
    <div className='max-h-[24rem] overflow-y-auto'>
      <div className='px-2 pb-2'>
        <SearchInput
          className='rounded-full border-none bg-grey-3'
          inputProps={{ className: 'bg-grey-3' }}
          placeholder='Search types…'
          setQuery={setQuery}
          query={query}
        />
      </div>
      <Listbox value={selected} onChange={onChange} multiple>
        <ListboxOptions static className='flex flex-col gap-0 px-1 pb-1'>
          {filtered.map((name) => (
            <ListboxOption
              key={name}
              value={name}
              className='cursor-pointer rounded-md px-1 py-0.5 hover:bg-[#00707010] active:bg-[#00707013] data-[focus]:bg-[#00707005]'
            >
              {({ selected: sel }) => (
                <div
                  className={classNames(
                    'flex items-center justify-between gap-2 rounded-md px-3 py-2 transition-colors',
                    sel ? 'bg-[#00707015]' : ''
                  )}
                >
                  <span
                    className={classNames(
                      'truncate text-sm',
                      sel && 'font-medium'
                    )}
                  >
                    {labelFor ? labelFor(name) : name}
                  </span>
                  <input
                    type='checkbox'
                    className='h-4 w-4 accent-brand'
                    checked={sel}
                    readOnly
                  />
                </div>
              )}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
    </div>
  );
}

export function NavTypesPicker({
  options,
  value,
  onChange,
  labelFor,
}: {
  options: string[];
  value: string[];
  onChange: (v: string[]) => void;
  labelFor?: (value: string) => string;
}) {
  // Items already provided as simple strings; no need to map to objects

  const sortRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Keep latest value/onChange in refs so sortable doesn't re-initialize mid-drag
  const valueRef = useRef<string[]>(value);
  const onChangeRef = useRef<(v: string[]) => void>(onChange);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const sortableOptions = useMemo(
    () => ({
      handle: '.chip-handle',
      animation: 150,
      delay: 250,
      delayOnTouchOnly: true,
      onUpdate({
        oldIndex,
        newIndex,
      }: {
        oldIndex?: number;
        newIndex?: number;
      }) {
        if (oldIndex !== undefined && newIndex !== undefined) {
          const next = move(valueRef.current, oldIndex, newIndex);
          onChangeRef.current(next);
        }
      },
    }),
    []
  );
  useSortable(sortRef.current, sortableOptions);

  const handleContainerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (
        target.closest('.nav-chip') ||
        target.closest('.chip-handle') ||
        target.closest('.nav-remove')
      ) {
        return;
      }
      buttonRef.current?.click();
    },
    []
  );

  return (
    <div className='relative'>
      <Popover className='relative'>
        <div
          data-usetiful='nav-types-picker'
          ref={containerRef}
          onClick={handleContainerClick}
          className={classNames(
            'input relative mt-0.5 w-full rounded border border-grey-20 bg-white pl-2 pr-8 focus-visible:border-grey-40 focus-visible:outline-none',
            value.length ? 'py-1.5' : 'py-2.5',
            'hover:border-grey-30 active:border-grey-40'
          )}
        >
          <div
            ref={sortRef}
            className='flex min-h-[28px] flex-wrap items-center gap-2'
          >
            {value.length === 0 ? (
              <span className='px-1 text-grey-40'>Select nav types…</span>
            ) : (
              value.map((t) => (
                <span
                  key={t}
                  className='nav-chip flex items-center rounded-md bg-grey-5 py-1.5 pl-2 pr-2 text-sm'
                >
                  <button
                    type='button'
                    className='chip-handle mr-1 cursor-grab select-none text-grey-40'
                    title='Drag to reorder'
                  >
                    ≡
                  </button>
                  <span className='px-1'>{labelFor ? labelFor(t) : t}</span>
                  <button
                    type='button'
                    className='nav-remove ml-1 flex h-5 w-5 items-center justify-center rounded-full text-lg text-grey-50 hover:bg-grey-10 hover:text-secondary'
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(value.filter((v) => v !== t));
                    }}
                    aria-label={`Remove ${labelFor ? labelFor(t) : t}`}
                  >
                    ×
                  </button>
                </span>
              ))
            )}
            <PopoverButton
              ref={buttonRef}
              type='button'
              className='pointer-events-auto absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 hover:bg-grey-10 active:bg-grey-20'
              aria-label='Add or select nav types'
            >
              <ChevronUpDownIcon
                className='h-5 w-5 flex-shrink-0 text-secondary hover:text-primary'
                aria-hidden='true'
              />
            </PopoverButton>
          </div>
          <PopoverPanel className='absolute left-0 right-0 top-[100%] z-[1000] mt-1 rounded-md bg-white py-2 text-base shadow-lg focus:outline-none'>
            <NavTypesListbox
              items={options}
              selected={value}
              onChange={useCallback(
                (newVal: string[]) => {
                  const keptInOrder = value.filter((name) =>
                    newVal.includes(name)
                  );
                  const newlyAdded = newVal.filter(
                    (name) => !value.includes(name)
                  );
                  onChange([...keptInOrder, ...newlyAdded]);
                },
                [value, onChange]
              )}
              labelFor={labelFor}
            />
          </PopoverPanel>
        </div>
      </Popover>
    </div>
  );
}
